import React, { useState, useEffect, useContext } from 'react';
import nteeMajors from '../../assets/ntee_major_names.json';
import nteeFullNames from '../../assets/ntee_full_names.json';
import { AuthContext } from '../../AuthContext';

function AwardsInsights({
    currentProgram,
    fetchedAwardsForNteeMajorNationally,
    setShowManageProgramModal,
    setShowLoginModal,
}) {    

    const { authUser } = useContext(AuthContext);

    const [activeRecipientState, setActiveRecipientState] = useState(null);
    const [activeRecipientNteeFull, setActiveRecipientNteeFull] = useState(null);

    useEffect(() => {
        if (currentProgram) {
            setActiveRecipientState(currentProgram?.primaryState);
            setActiveRecipientNteeFull(currentProgram?.nteeFull);
        }
    }, [currentProgram]);

    useEffect(() => {        
        if (!activeRecipientNteeFull || !activeRecipientState) return;
        
        const awardsForNteeMajorInState = fetchedAwardsForNteeMajorNationally.filter(award => 
            award.ntee_codes.ntee_major === activeRecipientNteeFull[0] && 
            award.state_abbreviation_cd === activeRecipientState
        );
        const awardsForNteeFullInState = fetchedAwardsForNteeMajorNationally.filter(award => 
            award.ntee_codes.ntee_full === activeRecipientNteeFull && 
            award.state_abbreviation_cd === activeRecipientState
        );
        setFetchedAwardsForNteeMajorInState(awardsForNteeMajorInState);
        setFetchedAwardsForNteeFullInState(awardsForNteeFullInState);
    }, [fetchedAwardsForNteeMajorNationally, activeRecipientNteeFull, activeRecipientState]);

    const [fetchedAwardsForNteeMajorInState, setFetchedAwardsForNteeMajorInState] = useState([]);
    const [fetchedAwardsForNteeFullInState, setFetchedAwardsForNteeFullInState] = useState([]);

    // Simple early return if data isn't ready yet
    if (!activeRecipientNteeFull || !activeRecipientState) {
        return <div className="py-12 text-center">Loading...</div>;
    }

    const groupedFundersForNteeMajorNationally = fetchedAwardsForNteeMajorNationally.reduce((acc, award) => {
        const { funder_ein, funder_name } = award;
        if (!acc[funder_ein]) {
            acc[funder_ein] = { funder_name, funder_ein, count: 0 };
        }
        acc[funder_ein].count += 1;
        return acc;
    }, {});

    const groupedRecipientsForNteeMajorNationally = fetchedAwardsForNteeMajorNationally.reduce((acc, award) => {
        const { recipient_ein, recipient_name, city_name, state_abbreviation_cd } = award;
        if (!acc[recipient_ein]) {
            acc[recipient_ein] = { recipient_name, recipient_ein, city_name, state_abbreviation_cd, count: 0 };
        }
        acc[recipient_ein].count += 1;
        acc[recipient_ein].total_cash_grant_amt += award.cash_grant_amt;
        acc[recipient_ein].average_cash_grant_amt = acc[recipient_ein].total_cash_grant_amt / acc[recipient_ein].count;
        return acc;
    }, {});


    const groupedFundersForNteeMajorInState = fetchedAwardsForNteeMajorInState.reduce((acc, award) => {
        const { funder_ein, funder_name } = award;
        if (!acc[funder_ein]) {
            acc[funder_ein] = { funder_name, funder_ein, count: 0 };
        }
        acc[funder_ein].count += 1;
        return acc;
    }, {});

    const groupedRecipientsForNteeMajorInState = fetchedAwardsForNteeMajorInState.reduce((acc, award) => {
        const { recipient_ein, recipient_name, city_name, state_abbreviation_cd } = award;
        if (!acc[recipient_ein]) {
            acc[recipient_ein] = { recipient_name, recipient_ein, city_name, state_abbreviation_cd, count: 0 };
        }
        acc[recipient_ein].count += 1;
        return acc;
    }, {});

    const groupedFundersForNteeFullInState = fetchedAwardsForNteeFullInState.reduce((acc, award) => {
        const { funder_ein, funder_name } = award;
        if (!acc[funder_ein]) {
            acc[funder_ein] = { funder_name, funder_ein, count: 0 };
        }
        acc[funder_ein].count += 1;
        return acc;
    }, {});

    const groupedRecipientsForNteeFullInState = fetchedAwardsForNteeFullInState.reduce((acc, award) => {    
        const { recipient_ein, recipient_name, city_name, state_abbreviation_cd, cash_grant_amt } = award;
        if (!acc[recipient_ein]) {
            acc[recipient_ein] = { 
                recipient_name, 
                recipient_ein, 
                city_name, 
                state_abbreviation_cd, 
                count: 0,
                total_cash_grant_amt: 0,
                average_cash_grant_amt: 0
            };
        }
        acc[recipient_ein].count += 1;
        acc[recipient_ein].total_cash_grant_amt += cash_grant_amt;
        acc[recipient_ein].average_cash_grant_amt = acc[recipient_ein].total_cash_grant_amt / acc[recipient_ein].count;
        return acc;
    }, {});




    const handleOrganizationClick = (ein) => {
        window.open(`/organizations/${ein}`, '_blank');
    }


    const handleProgramDetailClicked = () => {
        if (authUser) {
            setShowManageProgramModal(true);
        } else {
            alert("Login to customize your search criteria.");
            setShowLoginModal(true);
        }
    }

    // Helper function to get NTEE full description
    const getNteeFullDescription = (nteeCode) => {
        const fullEntry = nteeFullNames.find(entry => entry.ntee_code === nteeCode);
        return fullEntry ? fullEntry.description : nteeCode;
    };

    // Helper function to get NTEE major description
    const getNteeMajorDescription = (nteeMajor) => {
        const majorEntry = nteeMajors.find(major => major.ntee_major === nteeMajor);
        return majorEntry ? majorEntry.description : nteeMajor;
    };

    return (
        <div className="py-12 text-left">
        
        {/* Insights */}
        
        <div className="grid grid-cols-1 gap-8">
            {/* Funders Section */}
            <div>
                <h2 className="text-xl font-bold mb-4">Top funders of...</h2>
                <p className="font-bold mb-2"><span className="text-indigo-500 cursor-pointer" onClick={() => handleProgramDetailClicked()}>{getNteeFullDescription(activeRecipientNteeFull)}</span> nonprofits in <span className="text-indigo-500 cursor-pointer" onClick={() => handleProgramDetailClicked()}>{activeRecipientState}</span></p>
                <p></p>
                <div className="bg-gray-100 p-4 rounded-lg mb-4">
                    {Object.values(groupedFundersForNteeFullInState)
                        .sort((a, b) => b.count - a.count)
                        .slice(0, 5)
                        .map((funder) => (
                            <div 
                                key={funder.funder_ein} 
                                className="mb-2 cursor-pointer hover:bg-gray-200 p-2 rounded transition-colors"
                                onClick={() => handleOrganizationClick(funder.funder_ein)}
                            >
                                <p className="font-semibold">{funder.funder_name}</p>                                
                                <p className="text-sm text-gray-600">{funder.count} relevant awards granted</p>
                            </div>
                        ))}
                </div>
                <p className="font-bold mb-2"><span className="text-indigo-500 cursor-pointer" onClick={() => handleProgramDetailClicked()}>{getNteeMajorDescription(activeRecipientNteeFull[0])}</span> nonprofits in <span className="text-indigo-500 cursor-pointer" onClick={() => handleProgramDetailClicked()}>{activeRecipientState}</span></p>
                <div className="bg-gray-100 p-4 rounded-lg mb-4">
                    {Object.values(groupedFundersForNteeMajorInState)
                        .sort((a, b) => b.count - a.count)
                        .slice(0, 5)
                        .map((funder) => (
                            <div 
                                key={funder.funder_ein} 
                                className="mb-2 cursor-pointer hover:bg-gray-200 p-2 rounded transition-colors"
                                onClick={() => handleOrganizationClick(funder.funder_ein)}
                            >
                                <p className="font-semibold">{funder.funder_name}</p>                                
                                <p className="text-sm text-gray-600">{funder.count} relevant awards granted</p>
                            </div>
                        ))}
                </div>
                <p className="font-bold mb-2"><span className="text-indigo-500 cursor-pointer" onClick={() => handleProgramDetailClicked()}>{getNteeMajorDescription(activeRecipientNteeFull[0])}</span> nonprofits nationwide</p>
                <div className="bg-gray-100 p-4 rounded-lg mb-4">
                    {Object.values(groupedFundersForNteeMajorNationally)
                        .sort((a, b) => b.count - a.count)
                        .slice(0, 5)
                        .map((funder) => (
                            <div 
                                key={funder.funder_ein} 
                                className="mb-2 cursor-pointer hover:bg-gray-200 p-2 rounded transition-colors"
                                onClick={() => handleOrganizationClick(funder.funder_ein)}
                            >
                                <p className="font-semibold">{funder.funder_name}</p>
                                <p className="text-sm text-gray-600">{funder.count} relevant awards granted</p>
                            </div>
                        ))}
                </div>
            </div>

            {/* Recipients Section */}
            <div>
                <h2 className="text-xl font-bold mb-4">Top recipients of grants for...</h2>
                <p className="font-bold mb-2"><span className="text-indigo-500 cursor-pointer" onClick={() => handleProgramDetailClicked()}>{getNteeFullDescription(activeRecipientNteeFull)}</span> nonprofits in <span className="text-indigo-500 cursor-pointer" onClick={() => handleProgramDetailClicked()}>{activeRecipientState}</span></p>
                <div className="bg-gray-100 p-4 rounded-lg mb-4">
                    {Object.values(groupedRecipientsForNteeFullInState)
                        .sort((a, b) => b.count - a.count)
                        .slice(0, 5)
                        .map((recipient) => (
                            <div 
                                key={recipient.recipient_ein}
                                className="mb-2 cursor-pointer hover:bg-gray-200 p-2 rounded transition-colors"
                                onClick={() => handleOrganizationClick(recipient.recipient_ein)}
                            >
                                <p className="font-semibold">{recipient.recipient_name}</p>
                                <p className="text-sm text-gray-600">{recipient.city_name}, {recipient.state_abbreviation_cd}</p>
                                <p className="text-sm text-gray-600">{recipient.count} relevant awards received</p>
                                <p className="text-sm text-gray-600">${recipient.total_cash_grant_amt.toLocaleString()} total | ${recipient.average_cash_grant_amt.toLocaleString()} average</p>
                            </div>
                        ))}
                </div>
                
                <p className="font-bold mb-2"><span className="text-indigo-500 cursor-pointer" onClick={() => handleProgramDetailClicked()}>{getNteeMajorDescription(activeRecipientNteeFull[0])}</span> nonprofits in <span className="text-indigo-500 cursor-pointer" onClick={() => handleProgramDetailClicked()}>{activeRecipientState}</span></p>
                <div className="bg-gray-100 p-4 rounded-lg mb-4">
                    {Object.values(groupedRecipientsForNteeMajorInState)
                        .sort((a, b) => b.count - a.count)
                        .slice(0, 5)
                        .map((recipient) => (
                            <div 
                                key={recipient.recipient_ein}
                                className="mb-2 cursor-pointer hover:bg-gray-200 p-2 rounded transition-colors"
                                onClick={() => handleOrganizationClick(recipient.recipient_ein)}
                            >
                                <p className="font-semibold">{recipient.recipient_name}</p>
                                <p className="text-sm text-gray-600">{recipient.city_name}, {recipient.state_abbreviation_cd}</p>
                                <p className="text-sm text-gray-600">{recipient.count} relevant awards received</p>
                            </div>
                        ))}
                </div>
                <p className="font-bold mb-2"><span className="text-indigo-500 cursor-pointer" onClick={() => handleProgramDetailClicked()}>{getNteeMajorDescription(currentProgram?.nteeFull[0])}</span> nonprofits nationwide</p>
                <div className="bg-gray-100 p-4 rounded-lg mb-4">
                    {Object.values(groupedRecipientsForNteeMajorNationally)
                        .sort((a, b) => b.count - a.count)
                        .slice(0, 5)
                        .map((recipient) => (
                            <div 
                                key={recipient.recipient_ein}
                                className="mb-2 cursor-pointer hover:bg-gray-200 p-2 rounded transition-colors"
                                onClick={() => handleOrganizationClick(recipient.recipient_ein)}
                            >
                                <p className="font-semibold">{recipient.recipient_name}</p>
                                <p className="text-sm text-gray-600">{recipient.city_name}, {recipient.state_abbreviation_cd}</p>
                                <p className="text-sm text-gray-600">{recipient.count} relevant awards received</p>
                            </div>
                        ))}
                </div>
            </div>
        </div>
    </div>
    );
}

export default AwardsInsights;